import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setNotificationCount } from '../../../../actions/notification';
import axios from 'axios';
import Link from '../../Link/Link';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Divider from '@mui/material/Divider';
import Grow from '@mui/material/Grow';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import NotificationsIcon from '@mui/icons-material/NotificationsTwoTone';
import NoneIcon from '@mui/icons-material/NotInterested';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActiveTwoTone';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import { useTranslation } from 'react-i18next';
import DateDisplay from '../../DateDisplay/DateDisplay';
import Icon from '../../Icons/Icons';
import NotificationDialog from '../../../General/NotificationDialog';

export default function Notifications(props) {
    const navigate = useNavigate();
    const anchorRef = useRef(null);
    const dispatch = useDispatch();
    const auth = useSelector(state => state.auth);
    const isInternalAdmin = useSelector(state => state.auth.isInternalAdmin);
    const notification = useSelector(state => state.notification);
    const dateFormat = auth.user.dateFormat;
    const screenDefs = auth.screenDefs;
    const [open, setOpen] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [initialLoad, setInitialLoad] = useState(false);
    const { t } = useTranslation('translation');
    const { location } = props;

    const textNew = {
        fontWeight: 'bold'
    };
    const textUnread = {
        fontStyle: 'italic',
        fontWeight: 'bold'
    };
    const iconNew = {
        color: 'primary.main'
    };

    const loadData = useCallback(async () => {
        if(!isInternalAdmin){
            await axios.get('/api/notificationscount')
            .then(result => {
                dispatch(setNotificationCount({count: result.data.totalCount != null && result.data.totalCount !== '' ? result.data.totalCount : 0,
                                            lastFive: result.data.lastFive != null && result.data.lastFive !== '' ? result.data.lastFive : []}));
            })
            .catch(err => {
                dispatch({ type: 'CREATE_ALERT', payload: {message: err.response.data, title: t('error'), severity: 'error'}});
            });
        }
    }, [dispatch, t, isInternalAdmin]);

    useEffect(() => {
        if(!initialLoad){
            loadData();
            setInitialLoad(true);
        }
        const interval = setInterval(() => {
            loadData();
        }, 120000);
        return () => clearInterval(interval);
    }, [initialLoad, loadData]);

    const handleClose = () => {
        setOpen(false);
    };

    const handleToggle = () => {
        setOpen(prevOpen => !prevOpen);
    };

    const toggleDialog = () => {
        setOpenDialog(prevOpenDialog => !prevOpenDialog);
    };

    const clickMessage = (i, link) => {
        let record = notification.lastFive[i];
        // A notification from another tenant is shown but not navigable — its link resolves
        // against that tenant's data, which this session cannot reach. Switch tenants from the
        // profile menu to act on it.
        if(record.foreign) return;
        if(record.statusKey !== 'read') {
            axios.post('/api/notificationstatus/' + record._id, {statusKey: 'read'})
            .then(() => {
                navigate(link);
                loadData();
            })
            .catch(err => {
                dispatch({ type: 'CREATE_ALERT', payload: {message: err.response.data, title: t('error'), severity: 'error'}});
            });
        }else{
            navigate(link);
        }
        handleClose();
    };

    return (
        <>
            <IconButton
                onClick={handleToggle}
                id="notificationIcon"
                aria-haspopup="true"
                aria-controls={open ? 'notification-panel' : undefined}
                aria-expanded={open ? 'true' : undefined}
                color="inherit"
                ref={anchorRef}
            >
                <Badge overlap="rectangular" badgeContent={notification.count} color={location === 'bottom' ? 'primary' : 'secondary'}>
                    {notification.count > 0 ? <NotificationsActiveIcon /> : <NotificationsIcon />}
                </Badge>
            </IconButton>
            <Popper open={open} anchorEl={anchorRef.current} role={undefined} transition disablePortal>
            {({ TransitionProps, placement }) => (
                <Grow
                    {...TransitionProps}
                    sx={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}
                >
                    <Paper>
                        <ClickAwayListener onClickAway={handleClose}>
                            <div>
                                <Grid container sx={{ justifyContent: 'space-between' }}>
                                    <Grid>
                                        <Typography variant="h6" sx={{ padding: '3px 0px 0px 7px' }}>{t('notifications')}</Typography>
                                    </Grid>
                                    <Grid>
                                        <Button
                                            color="primary"
                                            startIcon={<NotificationsIcon />}
                                            sx={{ padding: '5px' }}
                                            onClick={toggleDialog}
                                        >
                                            {t('more')}
                                        </Button>
                                    </Grid>
                                </Grid>
                                <Divider variant="fullWidth" />
                                <List sx={{ width: '100%', maxWidth: 360, bgcolor: 'background.paper' }}>
                                    {notification.lastFive.length > 0 ?
                                        notification.lastFive.map((x, i) => (
                                            <React.Fragment key={'notification-list-' + i}>
                                                <ListItem>
                                                    <ListItemAvatar>
                                                        <Avatar sx={{ bgcolor: 'rgba(0, 0, 0, 0.12)' }}>
                                                            <Icon sx={x.statusKey === 'new' || x.statusKey === 'unread' ?
                                                                      iconNew : null} screenDefs={screenDefs} screenDef={x.screen != null && x.screen !== '' ? x.screen : ''} />
                                                        </Avatar>
                                                    </ListItemAvatar>
                                                    <ListItemText
                                                        disableTypography
                                                        primary={
                                                            <Typography sx={x.statusKey === 'new' ? textNew :
                                                                            x.statusKey === 'unread' ? textUnread : null}
                                                            >
                                                                {/* Rows from another tenant carry their real message but no link — see
                                                                    userService.decorateNotification. Rendering them as plain text rather
                                                                    than a dead Link keeps the affordance honest. */}
                                                                {x.foreign ?
                                                                    x.message :
                                                                    <Link onClick={() => clickMessage(i, x.link)} sx={{ cursor: 'pointer' }}>{x.message}</Link>
                                                                }
                                                            </Typography>
                                                        }
                                                        secondary={
                                                            <>
                                                                {/* Which tenant the notification came from sits directly under the
                                                                    message, ahead of the date — it is what tells the reader why the
                                                                    row is not clickable. */}
                                                                {x.foreign &&
                                                                    <Typography color="primary" variant="subtitle2" component="div"
                                                                                title={t('notifOtherTenant')}>
                                                                        {x.tenantName != null ? x.tenantName : t('otherTenant')}
                                                                    </Typography>
                                                                }
                                                                <Typography color="textSecondary" variant="subtitle2" component="div">
                                                                    <DateDisplay value={x.date} format={dateFormat}/>
                                                                </Typography>
                                                            </>
                                                        }
                                                    />
                                                </ListItem>
                                                {i !== 4 && <Divider variant="middle" />}
                                            </React.Fragment>
                                        )) :
                                        <ListItem>
                                            <ListItemAvatar>
                                                <Avatar>
                                                    <NoneIcon />
                                                </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText primary={t('none')} secondary={t('noNotifsAvailable')}/>
                                        </ListItem>
                                    }
                                </List>
                            </div>
                        </ClickAwayListener>
                    </Paper>
                </Grow>
            )}
            </Popper>
            {openDialog &&
                <NotificationDialog open={openDialog} toggleDialog={toggleDialog} />
            }
        </>
    );
}